import dayjs from 'dayjs';
import FormComponent from '../../../../components/FormComponent';
import { Events } from '../../../../utils/dom/html-events';
import Request, { ApiMode } from '../../../../utils/Request';
import EventHandler from '../../../../utils/dom/event-handler';
import SelectorEngine from '../../../../utils/dom/selector-engine';
import { startLoadingAnimation, stopLoadingAnimation } from '../../../../utils/dom/loading';
import HouseholdAddMember, { HOUSEHOLD_ADD_MEMBER_SUCCESS_EXIT } from '../../../../components/HouseholdAddMember';

const IS_LOCAL = false;

class HouseHoldMemberList extends FormComponent {
    markup(value, data, titleMarkup) {
        this.addPlayerLinkClass = 'js-trigger-add-new-household-member';
        return `
            <div class="lta-household-member-list u-flex u-flex-column u-mb-3 u-pt-0">
                <div class=${data.questionClass || 'u-subtitle1'}>${data.question}</div>

                ${data?.errorMessage !== '' ? this.showErrorContainer() : ''}

                <div class="js-player-details-header-description">${this.showPlayerDetailsHeaderDescription()}</div>

                <div class="js-info-dropdown">${this.showInfoDropdown()}</div>
                
                <div class="js-age-restriction-error">${this.showAgeRestrictionError()}</div>

                <div class="lta-household-players js-player-cards">${this.renderPlayerCards()}</div>

                <div class="js-already-booked-player-cards">${this.renderBookedPlayerCards()}</div>

                <div class="js-pending-invitation-player-card">${this.renderPendingInvitationPlayersCard()}</div>
        
                ${this.renderAddPlayerLink()}

                ${this.renderModalContent(data.supportingProps)}
            </div>
        `;
    }

    async fetchPlayersList(apiEndpoint, showGlobalLoader = true) {
        if (showGlobalLoader) startLoadingAnimation(true);
        try {
            const url = apiEndpoint;
            const response = await Request.get(url, {},
                {
                    local: IS_LOCAL,
                    req: {
                        apiMode: ApiMode.LtaError
                    }
                }
            );
            const {
                playerDetails,
                playerDetailsHeaderDescription,
                pendingInvitationPlayers,
                pendingInvitationHeaderDescription,
                bookedPlayerHeaderDescription,
                bookedPlayers,
                infoDropdownHeader,
                infoDropdownContent,
                showRefershButton,
                refreshPendingPlayerDetailsInfoText,
                refreshBtnLabel,
                loadingRefreshBtnlabel,
                maxSelectable,
                maxSelectionErrorMessage,
                showCheckbox,       
                error
            } = response;

            this.clearMaxSelectionError();
            this.latestPlayersData = playerDetails.map((player) => ({
                ...player,
                isChecked: player.isChecked || false
            }));
            this.maxSelectable = maxSelectable;
            this.maxSelectionErrorMessage = maxSelectionErrorMessage
            this.showCheckbox = showCheckbox;
            this.loadingRefreshBtnlabel = loadingRefreshBtnlabel

            if (playerDetailsHeaderDescription) {
                this.playerDetailsHeaderDescriptionWrapper = SelectorEngine.findOne(
                    '.js-player-details-header-description',
                    this.containerEl
                );
                this.infoDropdown = SelectorEngine.findOne('.js-info-dropdown', this.containerEl);
                if (this.playerDetailsHeaderDescriptionWrapper) {
                    this.playerDetailsHeaderDescriptionWrapper.innerHTML =
                        this.showPlayerDetailsHeaderDescription(playerDetailsHeaderDescription);
                }
                if (this.infoDropdown)
                    this.infoDropdown.innerHTML = this.showInfoDropdown(infoDropdownHeader, infoDropdownContent);
            }

            if (bookedPlayers) {
                this.bookedPlayersWrapper = SelectorEngine.findOne('.js-already-booked-player-cards', this.containerEl);
                if (this.bookedPlayersWrapper) {
                    this.bookedPlayersWrapper.innerHTML = this.renderBookedPlayerCards(
                        bookedPlayerHeaderDescription,
                        bookedPlayers
                    );
                }
            }

            if (error?.hasError) {
                this.errorWrapper = SelectorEngine.findOne('.js-age-restriction-error', this.containerEl);
                if (this.errorWrapper) {
                    this.errorWrapper.innerHTML = this.showAgeRestrictionError(error);
                }
            }

            if (playerDetails) {
                const playerDetailsCardsWrapper = SelectorEngine.findOne('.js-player-cards', this.containerEl);
                if (playerDetailsCardsWrapper) {
                    playerDetailsCardsWrapper.innerHTML = this.renderPlayerCards(this.latestPlayersData);
                }
            }

            if (pendingInvitationPlayers) {
                const pendingInvitationPlayersCardsWrapper = SelectorEngine.findOne(
                    '.js-pending-invitation-player-card',
                    this.containerEl
                );
                if (pendingInvitationPlayersCardsWrapper) {
                    pendingInvitationPlayersCardsWrapper.innerHTML = this.renderPendingInvitationPlayersCard(
                        pendingInvitationPlayers,
                        pendingInvitationHeaderDescription,
                        showRefershButton,
                        refreshPendingPlayerDetailsInfoText,
                        refreshBtnLabel
                    );
                }
            }
            this.validate();
        } catch (error) {
            console.log('Error :', error);
        } finally {
            if (showGlobalLoader) stopLoadingAnimation();
        }
    }

    showPlayerDetailsHeaderDescription(playerDetailsHeaderDescription) {
        if (!playerDetailsHeaderDescription) return '';
        return `<p class="lta-text-input__label u-text-grey-ui-mid u-my-1 md:u-my-2 u-body1">${playerDetailsHeaderDescription}</p>`;
    }
    showInfoDropdown(infoDropdownHeader, infoDropdownContent) {
        if (!infoDropdownHeader || !infoDropdownContent) return '';
        return `
        <div class="u-caption2 js-lta-tooltip" style="--lta-theme--1: var(--color-lta-blue-tint)">
            <div class="sm:u-flex md:u-flex xl:u-hstack js-lta-tooltip__trigger lta-cookies-modal-title2" tabindex="0">
                <svg
                    class="fe-icon u-mr-1"
                    width="1.5em"
                    height="1.5em"
                    fill="currentColor"
                    style="flex-shrink:0;"
                >
                    <use xlink:href="/static/images/icons/lta-icons.svg#info"></use>
                </svg>
                ${infoDropdownHeader}
            </div>
            <div class="js-lta-tooltip__content lta-cookies-modal-text u-body2 u-pt-1 u-pl-3">
                ${infoDropdownContent}
            </div>
        </div>`;
    }

    renderPlayerCards(playersDetails = []) {
        let html = '';

        const selectedCount = playersDetails.filter((p) => p.isChecked).length;

        if (playersDetails.length > 0) {
            html += `
            <div class="p-lta-player-list u-flex u-flex-column">
                ${playersDetails.map((player) => {
                    const isDisabled = this.maxSelectable > 0 && selectedCount >= this.maxSelectable && !player.isChecked;

                    return `
                        <div 
                            class="player-card lta-household-players--details-list-item u-flex u-align-center u-my-2 ${isDisabled ? 'is-disable' : ''} ${this.showCheckbox ? 'u-cursor-pointer' : ''}"
                            data-player-id="${player.id}"
                        >
                        ${this.showCheckbox
                            ? `<input 
                                    class="lta-form-check-input u-mr-2 js-player-checkbox" 
                                    type="checkbox" 
                                    name="householdPlayer" 
                                    data-player-id="${player.id}"
                                    id=${player?.id} 
                                    ${isDisabled ? 'disabled' : ''}
                                    ${player?.isChecked ? `checked` : ``}
                                >`
                            : ''
                        }

                        <div class="lta-household-players--details-list-card u-bg-background-blue u-p-2 u-px-3 u-py-2 u-rounded u-h-100 u-w-100 ${player.isChecked ? 'isSelected' : ''}">
                            <div class="u-caption1 u-text-grey-lta u-mb-0.5">
                                ${player.firstName} ${player.lastName}
                            </div>
                            <div class="u-flex u-align-center">
                                <div class="u-body1 u-text-grey-lta md:u-mr-1">
                                    ${player.gender},
                                </div>
                                <div class="u-body1 u-text-grey-lta">
                                    ${this.getAge(player.dateOfBirth)} years old
                                </div>
                            </div>
                        </div>
                    </div>`;
                }).join('')}
            </div>`;
        }
        return html;
    }

    renderBookedPlayerCards(bookedPlayerHeaderDescription = '', bookedPlayers = []) {
        let html = '';

        if (bookedPlayers.length > 0) {
            html += `
                <div class="p-lta-booked-player-list-description u-flex u-flex-column u-gap-2">
                    <div class="u-body1 u-text-grey-ui-mid u-mt-2">
                        ${bookedPlayerHeaderDescription}
                    </div>

                    ${bookedPlayers.map((player) => `<div class="p-lta-booked-player-list u-bg-grey-light u-flex u-flex-column u-py-2 u-px-3 u-rounded">
                            <div class="u-caption1">${player?.firstName} ${player?.lastName}</div>
                            <div class="u-body1">
                                ${[player?.gender, `${this.getAge(player.dateOfBirth)} years old`].filter(Boolean).join(', ')}
                            </div>
                        </div>
                    `).join('')}
                </div>
            `;
        }

        return html;
    }

    renderPendingInvitationPlayersCard(
        pendingInvitationPlayers,
        pendingInvitationHeaderDescription,
        showRefershButton = false,
        refreshPendingPlayerDetailsInfoText,
        refreshBtnLabel
    ) {
        let html = '';

        if (pendingInvitationPlayers?.length > 0) {
            html += `
                <div class="p-lta-ineligible-player-list u-flex u-flex-column u-gap-2">
                    <div class="u-body1 u-text-grey-ui-mid u-mt-2">
                        ${pendingInvitationHeaderDescription}
                    </div>

                    ${showRefershButton
                        ? `<div class="u-text-grey-ui-mid u-mt-1">
                                ${refreshPendingPlayerDetailsInfoText}
                            </div>

                            <button type="button" class="lta-btn lta-btn--line js-refresh-player-list">
                                ${refreshBtnLabel}
                                <div class="lta-icon--spinner js-refresh-loader u-hidden"></div>
                            </button>`
                        : ''
                    }

                    ${pendingInvitationPlayers.map((player) => `
                        <div class="p-lta-player-list u-bg-grey-light u-flex u-flex-column u-py-2 u-px-3 u-rounded">
                            <div class="u-caption1">${player?.firstName} ${player?.lastName}</div>
                            <div class="u-body1">
                                ${[player?.gender, `${this.getAge(player.dateOfBirth)} years old`].filter(Boolean).join(', ')}
                            </div>
                        </div>
                    `).join('')}
                </div>
            `;
        }   
        return html;
    }

    getAge(dateString) {
        const dateObject = dayjs(new Date());
        return dateObject.diff(dayjs(dateString), 'year');
    }

    showAgeRestrictionError(error) {
        if (!error?.errorMessageTitle) return '';

        return `
            <div class="p-add-player-error-container lta-snackbar lta-snackbar--hard-alert has-icon u-mb-3" style="min-width:100%;">
                <div class="u-flex u-flex-column u-gap-1">
                    <div class="p-add-player-error-content lta-snackbar__label u-caption2 u-fw-extrabold">${error.errorMessageTitle}</div>
                    <div class="p-add-player-error-description lta-snackbar__label u-body2">${error.errorMessageDescription}</div>
                </div>
                <div class="lta-snackbar__icon">
                    <svg class="lta-icon js-lta-text-input--btn-icon" width="1em" height="1em" style="color:currentColor;" fill="currentColor" alt="info" aria-hidden="true">
                        <use xlink:href="/static/images/icons/lta-icons.svg#info"></use>
                    </svg>
                </div> 
            </div>
        `;
    }

    showErrorContainer() {
        return `            
            <div class="p-add-player-error-container lta-snackbar lta-snackbar--hard-alert has-icon u-mb-3">
                <p class="p-add-player-error-content lta-snackbar__label title title">${this.data?.errorMessage}</p>
                <button type="button" class="lta-btn lta-btn__error-dismiss lta-btn--sm lta-btn--primary u-mt-1">Close</button>
                <div class="lta-snackbar__icon">
                    <svg class="lta-icon js-lta-text-input--btn-icon" width="1em" height="1em" style="color:currentColor;" fill="currentColor" alt="info" aria-hidden="true"><use xlink:href="/static/images/icons/lta-icons.svg#info"></use></svg>
                </div> 
            </div>
        `;
    }

    renderAddPlayerLink() {
        return `${
            this.data.supportingProps.allowNewHouseholdMember
                ? ` <button type="button" class="${this.addPlayerLinkClass} lta-btn lta-btn--lg lta-btn--link u-body1 u-text-lta-blue u-fw-bold u-mt-5">
                    ${this.data?.supportingProps?.addHouseholdMemberLinkText}
                </button>`
                : ''
        }`;
    }

    renderModalContent(data) {
        return `
            <div class="p-household-accounts-add-member-modal-wrppaer">
                <div class="p-household-accounts-add-member-page" data-exit-mode="page-controlled" data-household-add-member-form-steps-url=${data.getHouseholdMemberFormInit.endpoint} data-household-save-member-url=${data.getHouseholdMemberFormInit.addToHouseholdButtonApi.endpoint}>
                    <div class="lta-modal" id="p-household-accounts-add-member-page__modal" tabindex="-1" aria-labelledby="true" aria-modal="true" role="dialog">
                        <div class="lta-modal-dialog lta-modal-fullscreen">
                            <div class="lta-modal-content">
                                <div class="p-household-accounts-add-member-page__container">
                                    <div class="l-grid l-lta-grid">
                                        <div class="g-col-12 g-col-md-3 g-col-xl-4"></div>
                                        <div class="g-col-12 g-col-md-6 g-col-xl-4">
                                            <div class="xl:u-mt-15 md:u-mt-6">
                                                <div class="lta-snackbar lta-snackbar--alert js-household-accounts-add-member-page__error-container has-icon u-vstack u-hidden">
                                                    <p class="lta-snackbar__label title error-text" aria-label="true" onclick=""></p>
                                                    <button type="button" class="lta-btn lta-btn--sm lta-btn--inverse lta-btn--block u-ml-1 js-dismiss-error" dataset="">Close</button> 
                                                    <div class="lta-snackbar__icon">
                                                        <svg class="lta-icon js-lta-text-input--btn-icon" width="1em" height="1em" style="color:currentColor;" fill="currentColor" alt="alert" aria-hidden="true">
                                                            <use xlink:href="/static/images/icons/lta-icons.svg#alert"></use>
                                                        </svg>
                                                    </div>
                                                </div>
                                                <div class="p-household-accounts-add-member-page__questions">
                                                    
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="p-household-accounts-add-member-page__image" style="background-image: url(${data.getHouseholdMemberFormInit.formPageImagePath})"></div>
                                </div>
                                <div class="p-household-accounts-add-member-page__stripes xl:u-flex">
                                    <svg viewBox="0 0 217 58" class="lta-shape" xmlns="http://www.w3.org/2000/svg">
                                        <g fill="none" fill-rule="evenodd">
                                            <g fill="currentColor">
                                                <path d="M337.8 12530.31c-5.021.364-10.046-2.7-15.084-3.995-10.074-2.592-20.187-5.037-30.247-7.682-10.062-2.637-20.088-5.411-30.098-8.244l-7.508-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.027-2.771-15.043-4.145l-15.051-4.113c-5.017-1.371-10.04-2.716-15.067-4.053-5.027-1.333-10.057-2.657-15.092-3.961l-7.551-1.96-7.558-1.938-7.561-1.922-3.782-.957-3.785-.946c-.62-.156-1.241-.31-1.862-.465v.772l1.653.474 3.751 1.072 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.031 2.753 15.052 4.112 5.021 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.031 1.317 10.063 2.635 15.1 3.928l3.78.968 3.781.958 7.563 1.915c10.086 2.55 20.156 5.157 30.19 7.901 10.035 2.737 20.017 5.673 30.039 8.463 5.01 1.396 10.031 2.752 15.06 4.079 4.231 1.123 8.466 2.232 12.704 3.332h4.758l-2.05-.57c-5.012-1.39-10.028-2.764-15.049-4.12zm-182.8-52.66c1.594.46 3.189.917 4.784 1.373l3.754 1.072 3.756 1.062 7.516 2.115 7.52 2.098 7.525 2.078c5.017 1.387 10.039 2.754 15.063 4.114 5.025 1.354 10.053 2.7 15.087 4.02l15.103 3.962c5.036 1.317 10.07 2.636 15.112 3.93l3.782.968 3.784.959 7.569 1.916c10.093 2.55 20.17 5.159 30.211 7.904 10.043 2.738 20.033 5.675 30.061 8.466 5.014 1.396 10.039 2.753 15.071 4.08 5.031 1.335 10.066 2.652 15.106 3.954 4.281 1.106 8.566 2.202 12.855 3.279H372l-.846-.242c-5.004-1.433-10.016-2.836-15.032-4.226-5.016-1.39-10.036-2.765-15.06-4.123-5.023-1.364-10.053-2.7-15.094-3.996-10.082-2.593-20.203-5.039-30.27-7.685-10.07-2.639-20.102-5.414-30.12-8.248l-7.513-2.122-3.757-1.06-3.76-1.05c-5.013-1.4-10.034-2.773-15.053-4.148l-15.063-4.114c-5.02-1.372-10.048-2.718-15.078-4.055-5.03-1.333-10.064-2.658-15.103-3.963l-7.557-1.96-7.562-1.94-7.567-1.923-3.785-.957-3.787-.947c-1.664-.415-3.328-.829-4.993-1.241v.65zm164.443 52.549c-10.074-2.592-20.188-5.037-30.248-7.682-10.062-2.638-20.087-5.412-30.097-8.244l-7.508-2.121-3.754-1.06-3.758-1.05c-5.01-1.399-10.026-2.771-15.042-4.145l-15.051-4.113c-5.017-1.371-10.041-2.717-15.067-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.938-7.561-1.923-3.782-.956-2.374-.594v.891l2.131.61 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.699 15.077 4.018l15.092 3.96c5.031 1.317 10.062 2.635 15.1 3.928l3.78.968 3.78.958 7.564 1.915c10.086 2.55 20.156 5.157 30.19 7.9 10.035 2.738 20.017 5.674 30.038 8.464 4.32 1.203 8.649 2.376 12.983 3.527h5.417l-2.976-.807c-5.019-1.364-10.046-2.698-15.083-3.994zm-33.484-3.806c-10.062-2.638-20.087-5.412-30.097-8.245l-7.508-2.12-3.755-1.06-3.757-1.05c-5.01-1.399-10.026-2.772-15.042-4.146l-15.052-4.112c-5.016-1.372-10.04-2.717-15.066-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.939-7.561-1.922-2.92-.738v1l2.649.75 7.51 2.113 7.515 2.098 7.52 2.077c5.013 1.387 10.03 2.753 15.051 4.112 5.022 1.354 10.046 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.927l3.78.969 3.78.958 7.564 1.915c10.086 2.549 20.156 5.157 30.19 7.9 9.62 2.624 19.19 5.43 28.793 8.115h5.155c-1.189-.31-2.379-.62-3.569-.925-10.074-2.592-20.188-5.038-30.248-7.682zm-42.457 7.335l-7.468-2.11-3.734-1.055-3.737-1.044c-4.983-1.391-9.972-2.757-14.962-4.124l-14.97-4.09c-4.99-1.365-9.987-2.703-14.987-4.033-5-1.325-10.003-2.642-15.011-3.94l-7.511-1.948-6.122-1.57v1.325l5.757 1.608 7.48 2.066c4.986 1.38 9.977 2.738 14.97 4.09 4.996 1.346 9.993 2.685 14.997 3.997l15.011 3.939c5.005 1.31 10.01 2.62 15.02 3.906l3.759.963 3.761.952 7.524 1.904 1.718.436h3.017c-1.504-.423-3.008-.846-4.512-1.272zm-51.836-5.481c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.656-15.092-3.961l-6.507-1.688v1.48l6.111 1.688c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96 4.577 1.197h5.426c-3.205-.884-6.41-1.763-9.617-2.64l-15.05-4.113zm-20.774 2.776c-5.027-1.332-10.057-2.656-15.092-3.96l-.8-.208v1.576l.404.112c5.013 1.386 10.031 2.753 15.052 4.112 2.915.785 5.832 1.568 8.75 2.345h6.469c-4.923-1.345-9.852-2.665-14.783-3.977zm-2.92 3.42c-4.322-1.144-8.645-2.283-12.972-3.409v1.622c2.856.786 5.713 1.567 8.572 2.344h6.487l-2.087-.557zm20.872-2.813c-5.017-1.371-10.041-2.716-15.067-4.053-5.028-1.332-10.057-2.657-15.092-3.961l-3.685-.956v1.528l3.289.908c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.7 15.077 4.018l6.758 1.774h6l-12.331-3.37zm63.716-9.594l-7.508-2.12-3.754-1.061-3.757-1.05c-5.01-1.398-10.026-2.77-15.043-4.145l-15.05-4.112c-5.018-1.372-10.042-2.717-15.068-4.054-5.027-1.332-10.057-2.657-15.092-3.961l-7.551-1.96-7.557-1.938-7.18-1.825v1.098l6.858 1.93 7.514 2.098 7.52 2.077c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.1 3.927l3.78.969 3.781.958 7.564 1.915c10.085 2.549 20.155 5.157 30.189 7.9 5.035 1.374 10.057 2.797 15.077 4.227h3.597c-6.187-1.551-12.375-3.098-18.542-4.72-10.062-2.637-20.088-5.411-30.098-8.244zm26.812 12.132c-10.062-2.637-20.088-5.411-30.097-8.244l-7.509-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.026-2.77-15.042-4.145l-15.052-4.112c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.657-15.091-3.961l-7.552-1.96-7.557-1.938-3.894-.99v1.188l3.572 1.005 7.514 2.098 7.52 2.077c5.013 1.387 10.032 2.753 15.052 4.112 5.022 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.928l3.779.968 3.781.958 7.564 1.916c10.085 2.549 20.155 5.156 30.189 7.9l1.231.339h2.093c-1.064-.276-2.129-.552-3.192-.832zm-123.04-27.322l7.479 2.089 7.484 2.068c4.99 1.38 9.984 2.74 14.98 4.092 4.998 1.347 9.998 2.687 15.006 4l15.02 3.94c5.008 1.311 10.016 2.623 15.03 3.91l3.761.963 3.764.953 7.528 1.906c5.497 1.389 10.988 2.796 16.472 4.233h1.979c-6.085-1.657-12.16-3.352-18.227-5.07l-7.473-2.111-3.737-1.056-3.739-1.045c-4.986-1.392-9.979-2.758-14.971-4.126l-14.98-4.093c-4.994-1.365-9.994-2.704-14.996-4.035-5.004-1.326-10.01-2.644-15.02-3.942l-7.517-1.95-7.522-1.928-1.653-.42v1.248l1.332.374zm-1.332 8.74v-1.418l2.01.516 7.552 1.959c5.035 1.305 10.064 2.629 15.092 3.96 5.026 1.338 10.05 2.683 15.067 4.055l15.051 4.112c5.016 1.374 10.033 2.747 15.043 4.145l3.757 1.05 3.664 1.035h-4.399l-3.35-.859c-5.038-1.292-10.07-2.61-15.101-3.927l-15.092-3.96c-5.031-1.32-10.055-2.665-15.077-4.019-5.02-1.359-10.038-2.725-15.051-4.112l-7.52-2.077-1.646-.46z" transform="translate(-155 -12477)"></path>
                                            </g>
                                        </g>
                                    </svg>
                                </div>
                                <div class="lta-modal p-add-household-exit-confirmation-modal js-exit-household-add-member-confirm" id="" tabindex="-1" aria-labelledby="js-exit-household-add-member-confirm-label" aria-hidden="true">
                                    <div class="lta-modal-dialog">
                                        <div class="lta-modal-content">
                                            <button type="button" class="lta-btn lta-btn--lg lta-btn--link lta-btn--icon lta-btn--icon-after exit-link js-exit-household-member-modal-exit-btn u-flex u-text-grey-dark u-align-center u-justify-end u-px-2 u-pt-2">
                                                Exit
                                                <svg 
                                                    class="lta-icon js-lta-text-input--btn-icon u-ml-0" 
                                                    width="1.4em" height="1.4em" 
                                                    style="color:currentColor;" 
                                                    fill="currentColor" 
                                                    alt="close" 
                                                    aria-hidden="true"
                                                >
                                                    <use xlink:href="/static/images/icons/lta-icons.svg#close"></use>
                                                </svg>
                                            </button>
                                            <div class="lta-modal-header u-text-center u-flex u-flex-column u-gap-1">
                                                <div class="subtitle">Are you sure you want to</div>
                                                <div class="title" id="js-exit-household-add-member-confirm-label">Leave this page?</div>
                                            </div>
                                            <div class="lta-modal-body u-text-center">
                                                <div class="description u-my-2 md:u-my-3">Your changes will not be saved.</div>
                                                <div class="lta-modal-exit--actions">
                                                    <div class="lta-modal-exit--actions-item md:u-mr-1 u-mb-1 md:u-mb-0">
                                                        <button type="button" class="lta-btn lta-btn--default lta-btn--primary lta-btn--block js-exit-household-add-member-confirm-no u-text-white">
                                                            Stay on page
                                                        </button>
                                                    </div>
                                                    <div class="lta-modal-exit--actions-item">
                                                        <a class="lta-btn lta-btn--default lta-btn--secondary lta-btn--block js-exit-household-add-member-confirm-yes u-text-lta-dark-blue"> 
                                                            Leave page
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="lta-modal-bottom-angle">
                                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                                    <polygon points="0,0 100,0 100,100"></polygon>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="lta-page-layout__wrapper js-household-members-summary-page u-hidden">
                    <div class="lta-modal" id="p-page-layout__modal" tabindex="-1" aria-labelledby="true" aria-hidden="true">
                        <div class="lta-modal-dialog lta-modal-fullscreen">
                            <div class="lta-modal-content">
                                <div class="lta-page-layout__content-wrapper xl:u-pt-15 u-pt-5">
                                    <div class="l-grid l-lta-grid">
                                        <div class="g-col-12 g-col-md-3 g-col-xl-4"></div>
                                        <div class="g-col-12 g-col-md-6 g-col-xl-4">
                                            <div class="p-household-members-summary-container">
                                            <div class="lta-snackbar lta-snackbar--alert js-household-members-summary-page__error-container has-icon u-vstack u-mb-3 u-hidden">
                                                <p class="lta-snackbar__label title error-text" aria-label="true" onclick=""></p>
                                                <button type="button" class="lta-btn lta-btn--sm lta-btn--inverse lta-btn--block u-ml-1 js-dismiss-error" dataset="">Close</button> 
                                                <div class="lta-snackbar__icon">
                                                    <svg class="lta-icon js-lta-text-input--btn-icon" width="1em" height="1em" style="color:currentColor;" fill="currentColor" alt="alert" aria-hidden="true">
                                                        <use xlink:href="/static/images/icons/lta-icons.svg#alert"></use>
                                                    </svg>
                                                </div>
                                            </div>
                                            <div class="u-title-case1 u-text-grey-dark">${data.addToHouseholdReviewPageDetails.pageTitle}</div>
                                            <div class="p-household-accounts-add-member-page__selected-members js-household-members-wrapper u-py-3 u-flex u-flex-column u-gap-3" data-household-final-submit-url=${data.addToHouseholdReviewPageDetails.reviewHousholdButtonApi.endpoint}>
                                                <div class="u-flex u-flex-column u-gap-1">
                                                
                                                </div>
                                            </div>
                                            <button type="button" class="lta-btn lta-btn--default lta-btn--line lta-btn--block lta-btn--icon lta-btn--icon-before js-add-new-household-member-button">
                                                <svg class="lta-icon js-lta-text-input--btn-icon u-mr-1 " width="1.5em" height="1.5em" style="color:;" fill="" alt="plus" aria-hidden="true" ||="" currentcolor="">
                                                    <use xlink:href="/static/images/icons/lta-icons.svg#plus"></use>
                                                </svg>
                                                ${data.addToHouseholdReviewPageDetails.addMemberBtnText}
                                            </button>
                                            <hr class="u-my-3">
                                            <button type="button" class="lta-btn lta-btn--lg lta-btn--primary lta-btn--block js-final-household-members-submit-button">${data.addToHouseholdReviewPageDetails.submitBtnText}</button>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="lta-page-layout__image image-cut-align" style="background-image: url(${data.addToHouseholdReviewPageDetails.summaryPageImagePath})"></div>
                                </div>
                                <div class="lta-page-layout__stripes xl:u-flex" style="--lta-modal-page-layout-stripe-theme--: rgba(142, 85, 152, 0.30);">
                                    <svg viewBox="0 0 217 58" class="lta-shape" xmlns="http://www.w3.org/2000/svg">
                                        <g fill="none" fill-rule="evenodd">
                                            <g fill="currentColor">
                                                <path d="M337.8 12530.31c-5.021.364-10.046-2.7-15.084-3.995-10.074-2.592-20.187-5.037-30.247-7.682-10.062-2.637-20.088-5.411-30.098-8.244l-7.508-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.027-2.771-15.043-4.145l-15.051-4.113c-5.017-1.371-10.04-2.716-15.067-4.053-5.027-1.333-10.057-2.657-15.092-3.961l-7.551-1.96-7.558-1.938-7.561-1.922-3.782-.957-3.785-.946c-.62-.156-1.241-.31-1.862-.465v.772l1.653.474 3.751 1.072 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.031 2.753 15.052 4.112 5.021 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.031 1.317 10.063 2.635 15.1 3.928l3.78.968 3.781.958 7.563 1.915c10.086 2.55 20.156 5.157 30.19 7.901 10.035 2.737 20.017 5.673 30.039 8.463 5.01 1.396 10.031 2.752 15.06 4.079 4.231 1.123 8.466 2.232 12.704 3.332h4.758l-2.05-.57c-5.012-1.39-10.028-2.764-15.049-4.12zm-182.8-52.66c1.594.46 3.189.917 4.784 1.373l3.754 1.072 3.756 1.062 7.516 2.115 7.52 2.098 7.525 2.078c5.017 1.387 10.039 2.754 15.063 4.114 5.025 1.354 10.053 2.7 15.087 4.02l15.103 3.962c5.036 1.317 10.07 2.636 15.112 3.93l3.782.968 3.784.959 7.569 1.916c10.093 2.55 20.17 5.159 30.211 7.904 10.043 2.738 20.033 5.675 30.061 8.466 5.014 1.396 10.039 2.753 15.071 4.08 5.031 1.335 10.066 2.652 15.106 3.954 4.281 1.106 8.566 2.202 12.855 3.279H372l-.846-.242c-5.004-1.433-10.016-2.836-15.032-4.226-5.016-1.39-10.036-2.765-15.06-4.123-5.023-1.364-10.053-2.7-15.094-3.996-10.082-2.593-20.203-5.039-30.27-7.685-10.07-2.639-20.102-5.414-30.12-8.248l-7.513-2.122-3.757-1.06-3.76-1.05c-5.013-1.4-10.034-2.773-15.053-4.148l-15.063-4.114c-5.02-1.372-10.048-2.718-15.078-4.055-5.03-1.333-10.064-2.658-15.103-3.963l-7.557-1.96-7.562-1.94-7.567-1.923-3.785-.957-3.787-.947c-1.664-.415-3.328-.829-4.993-1.241v.65zm164.443 52.549c-10.074-2.592-20.188-5.037-30.248-7.682-10.062-2.638-20.087-5.412-30.097-8.244l-7.508-2.121-3.754-1.06-3.758-1.05c-5.01-1.399-10.026-2.771-15.042-4.145l-15.051-4.113c-5.017-1.371-10.041-2.717-15.067-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.938-7.561-1.923-3.782-.956-2.374-.594v.891l2.131.61 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.699 15.077 4.018l15.092 3.96c5.031 1.317 10.062 2.635 15.1 3.928l3.78.968 3.78.958 7.564 1.915c10.086 2.55 20.156 5.157 30.19 7.9 10.035 2.738 20.017 5.674 30.038 8.464 4.32 1.203 8.649 2.376 12.983 3.527h5.417l-2.976-.807c-5.019-1.364-10.046-2.698-15.083-3.994zm-33.484-3.806c-10.062-2.638-20.087-5.412-30.097-8.245l-7.508-2.12-3.755-1.06-3.757-1.05c-5.01-1.399-10.026-2.772-15.042-4.146l-15.052-4.112c-5.016-1.372-10.04-2.717-15.066-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.939-7.561-1.922-2.92-.738v1l2.649.75 7.51 2.113 7.515 2.098 7.52 2.077c5.013 1.387 10.03 2.753 15.051 4.112 5.022 1.354 10.046 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.927l3.78.969 3.78.958 7.564 1.915c10.086 2.549 20.156 5.157 30.19 7.9 9.62 2.624 19.19 5.43 28.793 8.115h5.155c-1.189-.31-2.379-.62-3.569-.925-10.074-2.592-20.188-5.038-30.248-7.682zm-42.457 7.335l-7.468-2.11-3.734-1.055-3.737-1.044c-4.983-1.391-9.972-2.757-14.962-4.124l-14.97-4.09c-4.99-1.365-9.987-2.703-14.987-4.033-5-1.325-10.003-2.642-15.011-3.94l-7.511-1.948-6.122-1.57v1.325l5.757 1.608 7.48 2.066c4.986 1.38 9.977 2.738 14.97 4.09 4.996 1.346 9.993 2.685 14.997 3.997l15.011 3.939c5.005 1.31 10.01 2.62 15.02 3.906l3.759.963 3.761.952 7.524 1.904 1.718.436h3.017c-1.504-.423-3.008-.846-4.512-1.272zm-51.836-5.481c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.656-15.092-3.961l-6.507-1.688v1.48l6.111 1.688c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96 4.577 1.197h5.426c-3.205-.884-6.41-1.763-9.617-2.64l-15.05-4.113zm-20.774 2.776c-5.027-1.332-10.057-2.656-15.092-3.96l-.8-.208v1.576l.404.112c5.013 1.386 10.031 2.753 15.052 4.112 2.915.785 5.832 1.568 8.75 2.345h6.469c-4.923-1.345-9.852-2.665-14.783-3.977zm-2.92 3.42c-4.322-1.144-8.645-2.283-12.972-3.409v1.622c2.856.786 5.713 1.567 8.572 2.344h6.487l-2.087-.557zm20.872-2.813c-5.017-1.371-10.041-2.716-15.067-4.053-5.028-1.332-10.057-2.657-15.092-3.961l-3.685-.956v1.528l3.289.908c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.7 15.077 4.018l6.758 1.774h6l-12.331-3.37zm63.716-9.594l-7.508-2.12-3.754-1.061-3.757-1.05c-5.01-1.398-10.026-2.77-15.043-4.145l-15.05-4.112c-5.018-1.372-10.042-2.717-15.068-4.054-5.027-1.332-10.057-2.657-15.092-3.961l-7.551-1.96-7.557-1.938-7.18-1.825v1.098l6.858 1.93 7.514 2.098 7.52 2.077c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.1 3.927l3.78.969 3.781.958 7.564 1.915c10.085 2.549 20.155 5.157 30.189 7.9 5.035 1.374 10.057 2.797 15.077 4.227h3.597c-6.187-1.551-12.375-3.098-18.542-4.72-10.062-2.637-20.088-5.411-30.098-8.244zm26.812 12.132c-10.062-2.637-20.088-5.411-30.097-8.244l-7.509-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.026-2.77-15.042-4.145l-15.052-4.112c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.657-15.091-3.961l-7.552-1.96-7.557-1.938-3.894-.99v1.188l3.572 1.005 7.514 2.098 7.52 2.077c5.013 1.387 10.032 2.753 15.052 4.112 5.022 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.928l3.779.968 3.781.958 7.564 1.916c10.085 2.549 20.155 5.156 30.189 7.9l1.231.339h2.093c-1.064-.276-2.129-.552-3.192-.832zm-123.04-27.322l7.479 2.089 7.484 2.068c4.99 1.38 9.984 2.74 14.98 4.092 4.998 1.347 9.998 2.687 15.006 4l15.02 3.94c5.008 1.311 10.016 2.623 15.03 3.91l3.761.963 3.764.953 7.528 1.906c5.497 1.389 10.988 2.796 16.472 4.233h1.979c-6.085-1.657-12.16-3.352-18.227-5.07l-7.473-2.111-3.737-1.056-3.739-1.045c-4.986-1.392-9.979-2.758-14.971-4.126l-14.98-4.093c-4.994-1.365-9.994-2.704-14.996-4.035-5.004-1.326-10.01-2.644-15.02-3.942l-7.517-1.95-7.522-1.928-1.653-.42v1.248l1.332.374zm-1.332 8.74v-1.418l2.01.516 7.552 1.959c5.035 1.305 10.064 2.629 15.092 3.96 5.026 1.338 10.05 2.683 15.067 4.055l15.051 4.112c5.016 1.374 10.033 2.747 15.043 4.145l3.757 1.05 3.664 1.035h-4.399l-3.35-.859c-5.038-1.292-10.07-2.61-15.101-3.927l-15.092-3.96c-5.031-1.32-10.055-2.665-15.077-4.019-5.02-1.359-10.038-2.725-15.051-4.112l-7.52-2.077-1.646-.46z" transform="translate(-155 -12477)"></path>
                                            </g>
                                        </g>
                                    </svg>
                                </div>
                            </div>
                            <div class="lta-modal-bottom-angle">
                                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                                    <polygon points="0,0 100,0 100,100"></polygon>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="lta-page-layout__wrapper p-household-member-success-page js-household-members-success-page u-hidden" style="--lta-modal-page-layout-background-theme--: var(--color-background-green);">
                    <div class="lta-modal" id="p-page-layout__modal" tabindex="-1" aria-labelledby="true" aria-hidden="true">
                        <div class="lta-modal-dialog lta-modal-fullscreen">
                            <div class="lta-modal-content">
                                <div class="lta-page-layout__content-wrapper xl:u-pt-15 u-pt-5">
                                    <div class="l-grid l-lta-grid">
                                        <div class="g-col-12 g-col-md-3 g-col-xl-4"></div>
                                        <div class="g-col-12 g-col-md-6 g-col-xl-4">
                                            <div class="p-household-members-success-container u-text-center">
                                                <div class="lta-memebrship-success--icon u-mb-2">
                                                    <svg class="lta-icon js-lta-text-input--btn-icon lta-check-icon" width="2.5rem" height="2.5rem" style="color:#6EB96F;" fill="#6EB96F" alt="check-circle-no-fill" aria-hidden="true">
                                                        <use xlink:href="/static/images/icons/lta-icons.svg#check-circle-no-fill"></use>
                                                    </svg>
                                                </div>
                                                <p class="u-mb-1 md:u-mb-2 u-label2 text-transform-uppercase">${data.addHouseholdMemberSuccessPageDetails.pageSubTitle}</p>
                                                <div class="p-household-member-success-page__heading u-title-case1 u-text-grey-dark">${data.addHouseholdMemberSuccessPageDetails.pageTitle}</div>
                                                <div class="js-success-screen-member-status-wrapper u-py-3 u-flex u-flex-column u-gap-3"></div>
                                                <a class="lta-btn lta-btn--lg lta-btn--primary lta-btn--block js-success-page-submit-button">${data.addHouseholdMemberSuccessPageDetails.actionButtonText}</a>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="lta-page-layout__image" style="background-image: url(${data.addHouseholdMemberSuccessPageDetails.successPageImagePath})"></div>
                                </div>
                                <div class="lta-page-layout__stripes xl:u-flex" style="--lta-modal-page-layout-stripe-theme--: #467BA5;">
                                    <svg viewBox="0 0 217 58" class="lta-shape" xmlns="http://www.w3.org/2000/svg">
                                        <g fill="none" fill-rule="evenodd">
                                            <g fill="currentColor">
                                                <path d="M337.8 12530.31c-5.021.364-10.046-2.7-15.084-3.995-10.074-2.592-20.187-5.037-30.247-7.682-10.062-2.637-20.088-5.411-30.098-8.244l-7.508-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.027-2.771-15.043-4.145l-15.051-4.113c-5.017-1.371-10.04-2.716-15.067-4.053-5.027-1.333-10.057-2.657-15.092-3.961l-7.551-1.96-7.558-1.938-7.561-1.922-3.782-.957-3.785-.946c-.62-.156-1.241-.31-1.862-.465v.772l1.653.474 3.751 1.072 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.031 2.753 15.052 4.112 5.021 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.031 1.317 10.063 2.635 15.1 3.928l3.78.968 3.781.958 7.563 1.915c10.086 2.55 20.156 5.157 30.19 7.901 10.035 2.737 20.017 5.673 30.039 8.463 5.01 1.396 10.031 2.752 15.06 4.079 4.231 1.123 8.466 2.232 12.704 3.332h4.758l-2.05-.57c-5.012-1.39-10.028-2.764-15.049-4.12zm-182.8-52.66c1.594.46 3.189.917 4.784 1.373l3.754 1.072 3.756 1.062 7.516 2.115 7.52 2.098 7.525 2.078c5.017 1.387 10.039 2.754 15.063 4.114 5.025 1.354 10.053 2.7 15.087 4.02l15.103 3.962c5.036 1.317 10.07 2.636 15.112 3.93l3.782.968 3.784.959 7.569 1.916c10.093 2.55 20.17 5.159 30.211 7.904 10.043 2.738 20.033 5.675 30.061 8.466 5.014 1.396 10.039 2.753 15.071 4.08 5.031 1.335 10.066 2.652 15.106 3.954 4.281 1.106 8.566 2.202 12.855 3.279H372l-.846-.242c-5.004-1.433-10.016-2.836-15.032-4.226-5.016-1.39-10.036-2.765-15.06-4.123-5.023-1.364-10.053-2.7-15.094-3.996-10.082-2.593-20.203-5.039-30.27-7.685-10.07-2.639-20.102-5.414-30.12-8.248l-7.513-2.122-3.757-1.06-3.76-1.05c-5.013-1.4-10.034-2.773-15.053-4.148l-15.063-4.114c-5.02-1.372-10.048-2.718-15.078-4.055-5.03-1.333-10.064-2.658-15.103-3.963l-7.557-1.96-7.562-1.94-7.567-1.923-3.785-.957-3.787-.947c-1.664-.415-3.328-.829-4.993-1.241v.65zm164.443 52.549c-10.074-2.592-20.188-5.037-30.248-7.682-10.062-2.638-20.087-5.412-30.097-8.244l-7.508-2.121-3.754-1.06-3.758-1.05c-5.01-1.399-10.026-2.771-15.042-4.145l-15.051-4.113c-5.017-1.371-10.041-2.717-15.067-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.938-7.561-1.923-3.782-.956-2.374-.594v.891l2.131.61 3.754 1.061 7.51 2.114 7.515 2.098 7.52 2.077c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.699 15.077 4.018l15.092 3.96c5.031 1.317 10.062 2.635 15.1 3.928l3.78.968 3.78.958 7.564 1.915c10.086 2.55 20.156 5.157 30.19 7.9 10.035 2.738 20.017 5.674 30.038 8.464 4.32 1.203 8.649 2.376 12.983 3.527h5.417l-2.976-.807c-5.019-1.364-10.046-2.698-15.083-3.994zm-33.484-3.806c-10.062-2.638-20.087-5.412-30.097-8.245l-7.508-2.12-3.755-1.06-3.757-1.05c-5.01-1.399-10.026-2.772-15.042-4.146l-15.052-4.112c-5.016-1.372-10.04-2.717-15.066-4.054-5.028-1.332-10.057-2.656-15.092-3.96l-7.552-1.96-7.557-1.939-7.561-1.922-2.92-.738v1l2.649.75 7.51 2.113 7.515 2.098 7.52 2.077c5.013 1.387 10.03 2.753 15.051 4.112 5.022 1.354 10.046 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.927l3.78.969 3.78.958 7.564 1.915c10.086 2.549 20.156 5.157 30.19 7.9 9.62 2.624 19.19 5.43 28.793 8.115h5.155c-1.189-.31-2.379-.62-3.569-.925-10.074-2.592-20.188-5.038-30.248-7.682zm-42.457 7.335l-7.468-2.11-3.734-1.055-3.737-1.044c-4.983-1.391-9.972-2.757-14.962-4.124l-14.97-4.09c-4.99-1.365-9.987-2.703-14.987-4.033-5-1.325-10.003-2.642-15.011-3.94l-7.511-1.948-6.122-1.57v1.325l5.757 1.608 7.48 2.066c4.986 1.38 9.977 2.738 14.97 4.09 4.996 1.346 9.993 2.685 14.997 3.997l15.011 3.939c5.005 1.31 10.01 2.62 15.02 3.906l3.759.963 3.761.952 7.524 1.904 1.718.436h3.017c-1.504-.423-3.008-.846-4.512-1.272zm-51.836-5.481c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.656-15.092-3.961l-6.507-1.688v1.48l6.111 1.688c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96 4.577 1.197h5.426c-3.205-.884-6.41-1.763-9.617-2.64l-15.05-4.113zm-20.774 2.776c-5.027-1.332-10.057-2.656-15.092-3.96l-.8-.208v1.576l.404.112c5.013 1.386 10.031 2.753 15.052 4.112 2.915.785 5.832 1.568 8.75 2.345h6.469c-4.923-1.345-9.852-2.665-14.783-3.977zm-2.92 3.42c-4.322-1.144-8.645-2.283-12.972-3.409v1.622c2.856.786 5.713 1.567 8.572 2.344h6.487l-2.087-.557zm20.872-2.813c-5.017-1.371-10.041-2.716-15.067-4.053-5.028-1.332-10.057-2.657-15.092-3.961l-3.685-.956v1.528l3.289.908c5.013 1.386 10.03 2.753 15.051 4.112 5.022 1.353 10.046 2.7 15.077 4.018l6.758 1.774h6l-12.331-3.37zm63.716-9.594l-7.508-2.12-3.754-1.061-3.757-1.05c-5.01-1.398-10.026-2.77-15.043-4.145l-15.05-4.112c-5.018-1.372-10.042-2.717-15.068-4.054-5.027-1.332-10.057-2.657-15.092-3.961l-7.551-1.96-7.557-1.938-7.18-1.825v1.098l6.858 1.93 7.514 2.098 7.52 2.077c5.013 1.387 10.031 2.753 15.052 4.112 5.021 1.354 10.045 2.7 15.076 4.019l15.092 3.96c5.032 1.317 10.063 2.635 15.1 3.927l3.78.969 3.781.958 7.564 1.915c10.085 2.549 20.155 5.157 30.189 7.9 5.035 1.374 10.057 2.797 15.077 4.227h3.597c-6.187-1.551-12.375-3.098-18.542-4.72-10.062-2.637-20.088-5.411-30.098-8.244zm26.812 12.132c-10.062-2.637-20.088-5.411-30.097-8.244l-7.509-2.121-3.754-1.06-3.757-1.05c-5.01-1.398-10.026-2.77-15.042-4.145l-15.052-4.112c-5.017-1.372-10.04-2.717-15.067-4.054-5.027-1.332-10.057-2.657-15.091-3.961l-7.552-1.96-7.557-1.938-3.894-.99v1.188l3.572 1.005 7.514 2.098 7.52 2.077c5.013 1.387 10.032 2.753 15.052 4.112 5.022 1.353 10.045 2.7 15.076 4.018l15.092 3.96c5.032 1.317 10.063 2.635 15.101 3.928l3.779.968 3.781.958 7.564 1.916c10.085 2.549 20.155 5.156 30.189 7.9l1.231.339h2.093c-1.064-.276-2.129-.552-3.192-.832zm-123.04-27.322l7.479 2.089 7.484 2.068c4.99 1.38 9.984 2.74 14.98 4.092 4.998 1.347 9.998 2.687 15.006 4l15.02 3.94c5.008 1.311 10.016 2.623 15.03 3.91l3.761.963 3.764.953 7.528 1.906c5.497 1.389 10.988 2.796 16.472 4.233h1.979c-6.085-1.657-12.16-3.352-18.227-5.07l-7.473-2.111-3.737-1.056-3.739-1.045c-4.986-1.392-9.979-2.758-14.971-4.126l-14.98-4.093c-4.994-1.365-9.994-2.704-14.996-4.035-5.004-1.326-10.01-2.644-15.02-3.942l-7.517-1.95-7.522-1.928-1.653-.42v1.248l1.332.374zm-1.332 8.74v-1.418l2.01.516 7.552 1.959c5.035 1.305 10.064 2.629 15.092 3.96 5.026 1.338 10.05 2.683 15.067 4.055l15.051 4.112c5.016 1.374 10.033 2.747 15.043 4.145l3.757 1.05 3.664 1.035h-4.399l-3.35-.859c-5.038-1.292-10.07-2.61-15.101-3.927l-15.092-3.96c-5.031-1.32-10.055-2.665-15.077-4.019-5.02-1.359-10.038-2.725-15.051-4.112l-7.52-2.077-1.646-.46z" transform="translate(-155 -12477)"></path>
                                            </g>
                                        </g>
                                    </svg>
                                </div>
                            </div>
                            <div class="lta-modal-bottom-angle">
                                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                                    <polygon points="0,0 100,0 100,100"></polygon>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }

    afterRender() {
        const addHouseholdMemberSelector = SelectorEngine.findOne('.js-trigger-add-new-household-member', this.containerEl);

        this.addHouseholdModal = new HouseholdAddMember('.p-household-accounts-add-member-modal-wrppaer');
        this.fetchPlayersList(this.data.supportingProps.getHouseholdMemberDetails.endpoint, true);
        document.addEventListener(HOUSEHOLD_ADD_MEMBER_SUCCESS_EXIT, () => {
            this.clearMaxSelectionError();
            this.fetchPlayersList(this.data.supportingProps.getHouseholdMemberDetails.endpoint, true);
        });

        EventHandler.on(addHouseholdMemberSelector, Events.Click, () => {
            if (this.addHouseholdModal.isReady()) {
                this.addHouseholdModal.open();
            }
        });

        // toottip trigger events
        EventHandler.on(this.containerEl, 'click', '.js-lta-tooltip__trigger', (e) => {
            this.clickHandler(e);
        });

        EventHandler.on(this.containerEl, 'keypress', '.js-lta-tooltip__trigger', (e) => {
            if (e.key === 'Enter') this.clickHandler(e);
        });

        // Conditional refresh button event handler
        EventHandler.on(this.containerEl, 'click', '.js-refresh-player-list', async () => {
            this.setRefreshButtonState(true);
            this.setPageInteraction(false); // disable page clicks

            try {
                await this.fetchPlayersList(
                    this.data.supportingProps.getHouseholdMemberDetails.endpoint,
                    false
                );
            } finally {
                this.setRefreshButtonState(false);
                this.setPageInteraction(true); // enable again
            }
        });
        this.initSelectionHandler();
    }

    setPageInteraction(enable) {
        const container = this.containerEl;

        if (!enable) {
            container.style.pointerEvents = 'none';
        } else {
            container.style.pointerEvents = '';
        }
    }

    setRefreshButtonState(isLoading) {
        const refreshBtn = SelectorEngine.findOne('.js-refresh-player-list', this.containerEl);
        if (!refreshBtn) return;

        refreshBtn.disabled = isLoading;

        if (isLoading) {
            // Store original HTML
            refreshBtn.dataset.originalHtml = refreshBtn.innerHTML;
            refreshBtn.innerHTML = `
                <div class="lta-icon--spinner u-mr-1"></div>
                ${this.loadingRefreshBtnlabel}
            `;
        } else {
            // Restore original HTML
            refreshBtn.innerHTML = refreshBtn.dataset.originalHtml || 'Refresh';
        }
    }

    clickHandler(event) {
        const trigger = event.target.closest('.js-lta-tooltip__trigger');
        if (!trigger) return;

        const content = trigger.nextElementSibling;
        if (!content) return;

        content.classList.toggle('is-visible');
    }

    initSelectionHandler() {
        const container = this.containerEl;

        // Checkbox change
        EventHandler.on(container, 'click', '.js-player-checkbox', (e) => {
            e.stopPropagation();

            const playerId = e.target.getAttribute('data-player-id');

            this.updatePlayerSelection(playerId, e.target.checked);
        });

        // // Card click
        EventHandler.on(container, 'click', '.player-card', (e) => {
            // FIX: ignore checkbox clicks
            if (e.target.closest('.js-player-checkbox')) return;

            const card = e.target.closest('.player-card');
            if (!card) return;

            const checkbox = SelectorEngine.findOne('.js-player-checkbox', card);
            if (!checkbox || checkbox.disabled) return;

            checkbox.checked = !checkbox.checked;

            const playerId = card.getAttribute('data-player-id');
            this.updatePlayerSelection(playerId, checkbox.checked);
        });
    }

    updatePlayerSelection(playerId, isChecked) {
        const players = this.latestPlayersData;

        const selectedCount = players.filter((p) => p.isChecked).length;

        if (isChecked && this.maxSelectable > 0 && selectedCount >= this.maxSelectable) {
            this.showMaxSelectionError();
            return;
        }

        const player = players.find((p) => p.id == playerId);
        if (player) {
            player.isChecked = isChecked;
        }

        const updatedSelectedCount = players.filter((p) => p.isChecked).length;

        if (this.maxSelectable > 0 && updatedSelectedCount >= this.maxSelectable) {
            this.showMaxSelectionError();
        } else {
            this.clearMaxSelectionError();
        }

        this.refreshPlayerCards();
        this.triggerValueChange();

        this.validate();
    }

    clearMaxSelectionError() {
        const errorWrapper = SelectorEngine.findOne('.js-age-restriction-error', this.containerEl);
        if (errorWrapper) {
            errorWrapper.innerHTML = '';
        }
    }

    triggerValueChange() {
        const selectedPlayers = this.latestPlayersData.filter((p) => p.isChecked);

        this.value = {
            ...this.data,
            value: selectedPlayers
        };
    }

    refreshPlayerCards() {
        const wrapper = SelectorEngine.findOne('.js-player-cards', this.containerEl);
        if (wrapper) {
            wrapper.innerHTML = this.renderPlayerCards(this.latestPlayersData);
        }
    }

    showMaxSelectionError() {
        const errorWrapper = SelectorEngine.findOne('.js-age-restriction-error', this.containerEl);

        if (errorWrapper) {
            errorWrapper.innerHTML = `
                <div class="lta-snackbar lta-snackbar--warning has-icon u-mt-2 u-w-100" style="min-width:100%">
                    <p class="lta-snackbar__label title u-fw-semibold">
                        ${this.maxSelectionErrorMessage}
                    </p>
                    <div class="lta-snackbar__icon">
                        <svg class="lta-icon" width="1em" height="1em" fill="currentColor">
                            <use xlink:href="/static/images/icons/lta-icons.svg#info"></use>
                        </svg>
                    </div> 
                </div>
            `;
        }
    }

    toggleNextButton(enable) {
        const nextBtn = document.querySelector('.js-form-generator-next-button');

        if (!nextBtn) return;

        nextBtn.disabled = !enable;

        if (!enable) {
            nextBtn.classList.add('is-disabled');
        } else {
            nextBtn.classList.remove('is-disabled');
        }
    }

    validate() {
        // Skip if optional
        if (this.data?.isOptional) return true;

        const selectedPlayers = this.latestPlayersData?.filter((p) => p.isChecked) || [];

        const isValid = selectedPlayers.length >= 1;

        this.toggleNextButton(isValid);

        return isValid;
    }
}

export default HouseHoldMemberList;

